import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import fallbackRates from '../assets/data/currencies/eurofxref.json';
import { ECB_DAILY_URL, parseDailyRates } from '../lib/ecbFx';
import './FinanceTicker.css';

function fallbackFromBundledSnapshot() {
  return Object.entries(fallbackRates.cube)
    .filter(([code]) => code !== 'EUR')
    .map(([currency, rate]) => ({ currency, rate }));
}

export default function FinanceTicker() {
  const [rates, setRates] = useState(fallbackFromBundledSnapshot());
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);

    fetch(ECB_DAILY_URL, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`ECB feed responded ${res.status}`);
        return res.text();
      })
      .then((xml) => {
        const parsed = parseDailyRates(xml);
        if (parsed.length) {
          setRates(parsed);
          setIsLive(true);
        }
      })
      .catch((err) => {
        // Network failure, CORS block, or an unexpected payload — fall back
        // to the bundled snapshot rather than showing a broken widget.
        console.warn('Falling back to bundled currency snapshot:', err.message);
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  const track = [...rates, ...rates]; // duplicated for a seamless CSS loop

  return (
    <div className='finance-ticker' role='region' aria-label='Live currency exchange rates'>
      <div className='finance-ticker__track'>
        {track.map((r, idx) => (
          <Link
            className='finance-ticker__item'
            to={`/finances?currency=${r.currency}`}
            key={`${r.currency}-${idx}`}
            title={`View EUR/${r.currency} rate history`}
          >
            <strong>EUR/{r.currency}</strong> {r.rate.toFixed(4)}
          </Link>
        ))}
      </div>
      <p className='finance-ticker__source'>
        {isLive
          ? `Live reference rates from the European Central Bank (${rates.length} currencies) — click any pair for its history`
          : 'Reference rates (offline snapshot) — source: European Central Bank'}
      </p>
    </div>
  );
}
